import { AnimatePresence } from "framer-motion"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { useFilters, useFlexLayout, useGlobalFilter, useResizeColumns, useSortBy, useTable } from "react-table"
import styled from "styled-components"

import {
  ARTICLE_CODE_COLUMN_WIDTH,
  attributeIDs,
  SEARCHABLE_CONTENT_COLUMN_ID,
  SYSTEMS_COLUMN_FILTER_FUNCTION,
  SYSTEMS_COLUMN_ID,
} from "../../common/constants"
import { useArticlesFilter } from "../../context/ArticlesFilterContext"
import { useSystemsFilter } from "../../context/SystemsFilterContext"
import { useViewportSize } from "../../context/ViewportSizeContext"

import { useRecoilState } from "recoil"
import { useBrandedTranslation } from "../../../../../common/hooks/useBrandedTranslation"
import useIntersectionObserver from "../../../../../common/hooks/useIntersectionObserver"
import { activeArticleTableRowState } from "../../../../../common/store/articleTableStore"
import Feedback from "../../components/Feedback"
import prepareDataForTableRender from "../../helpers/prepare-data-for-table-render"
import OrderRelatedInfo from "../OrderRelatedInfo"
import Spacer from "../Spacer"
import Header from "./Header"
import Row from "./Row"

const ArticleTable = styled.div`
  position: relative;

  color: ${({ theme }) => theme.colors.black};
  font-family: ${({ theme }) => theme.fontFamily.geberitRg};
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  min-width: ${({ width }) => width}px !important; //override styles that are provided by the react-table plugin

  &.table--scrollable-right:after,
  &.table--scrollable-left:before {
    position: absolute;
    content: " ";
    display: block;
    width: 6px;
    top: 0;
    bottom: 0;
  }

  &.table--scrollable-right:after {
    right: 0;
    box-shadow: inset -11px 0 6px -11px ${({ theme }) => theme.colors.gray[500]};
  }

  &.table--scrollable-left:before {
    z-index: 1;
    left: ${ARTICLE_CODE_COLUMN_WIDTH}px;
    box-shadow: inset 11px 0 6px -11px ${({ theme }) => theme.colors.gray[500]};
  }
`

const TableBody = styled.div`
  overflow-x: auto;
  overflow-y: hidden;

  /* Article table styles for Firefox only to handle three dot menu and overflow on the table */
  @-moz-document url-prefix() {
    &.table--add-overflow-clip {
      overflow: clip;
    }

    /* Extra padding to stop the content shift when we remove the scrollbar */
    &.table--extra-padding-bot {
      padding-bottom: ${(props) => props.extraPaddingVal}px;
    }
  }
`

const Table = ({ articles, viewportEl, widthOffsets, highlightedArticles, attributesTypeSelection }) => {
  const { t } = useBrandedTranslation()
  const windowSize = useViewportSize()
  const systemFilter = useSystemsFilter()
  const [activeArticleTableRow] = useRecoilState(activeArticleTableRowState)

  const productData = useMemo(() => prepareDataForTableRender(articles, t("pdp-article-nubmer")), [articles])
  //this column contains info that is not in the columns, but is still searchable
  //e.g replacements, fractions
  const DEFAULT_HIDDEN_COLUMNS = [SEARCHABLE_CONTENT_COLUMN_ID, SYSTEMS_COLUMN_ID]
  //memoize article table data as recommended by react-table
  const columns = useMemo(() => productData.columns, [productData])
  const data = useMemo(() => productData.data, [productData])

  const tableContainerRef = React.useRef()
  const tableHeaderRef = React.createRef() //create ref is used when dealing with components
  const startArticleTableRef = React.createRef()
  const scheduledAnimationFrame = React.useRef(false) //track if there is scheduled animationframe to improve perf
  const initialContentWidth = columns.reduce((sum, col) => {
    return sum + col.width
  }, 0)

  const [contentWidth, setContentWidth] = useState(initialContentWidth)

  const viewportWidthInitial = viewportEl?.offsetWidth - widthOffsets

  const [viewportWidth, setViewportWidth] = useState(viewportWidthInitial)
  const [isScrollableLeft, setScrollableLeft] = useState(false)
  const [isScrollableRight, setScrollableRight] = useState(initialContentWidth > viewportWidthInitial)

  const articlesFilter = useArticlesFilter()

  const handleScroll = (e, ref) => {
    if (scheduledAnimationFrame.current) {
      return
    }

    scheduledAnimationFrame.current = true

    window.requestAnimationFrame(() => {
      const scrollLeft = e.nativeEvent.target.scrollLeft
      scheduledAnimationFrame.current = false
      ref.current.scrollLeft = scrollLeft

      setScrollableRight(!(scrollLeft + viewportWidth >= contentWidth - 5))
      setScrollableLeft(!(scrollLeft < 5))
    })
  }

  const handleTableScroll = (e) => {
    handleScroll(e, tableHeaderRef)
  }

  const handleTableHeaderScroll = (e) => {
    handleScroll(e, tableContainerRef)
  }

  useEffect(() => {
    const viewportWidth = viewportEl?.offsetWidth - widthOffsets

    setViewportWidth(viewportWidth)
    setScrollableRight(contentWidth > viewportWidth)
  }, [JSON.stringify(windowSize)])

  const filterTypes = React.useMemo(
    () => ({
      [SYSTEMS_COLUMN_FILTER_FUNCTION]: (rows, id, filterValue) => {
        if (!filterValue || filterValue === t("pdp-article-valid-for-systems-title-all-systems")) return rows
        return rows.filter((row) => {
          return row?.values?.systems?.includes(filterValue)
        })
      },
    }),
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setFilter,
    setGlobalFilter,
    state,
    setHiddenColumns,
  } = useTable(
    {
      columns,
      data,
      disableSortRemove: true, //disable the unordered state when the user starts sorting
      initialState: {
        hiddenColumns: [...DEFAULT_HIDDEN_COLUMNS],
      },
      filterTypes,
    },
    useFlexLayout,
    useGlobalFilter,
    useFilters,
    useSortBy,
    useResizeColumns
  )

  useEffect(() => {
    const hiddenColumns = columns
      .filter((column) => !attributesTypeSelection.includes(column.type))
      .map((column) => column.accessor)

    //hide columns that match the selection filter
    setHiddenColumns([...DEFAULT_HIDDEN_COLUMNS, ...hiddenColumns])

    const contentWidth = columns
      .filter((column) => attributesTypeSelection.includes(column.type))
      .reduce((contentWidth, col) => {
        return contentWidth + col.width
      }, 0)

    //update content width not to include the hidden columns width
    setContentWidth(contentWidth)

    const viewportWidth = viewportEl?.offsetWidth - widthOffsets
    setScrollableRight(contentWidth > viewportWidth)
  }, [attributesTypeSelection])

  useEffect(() => {
    setGlobalFilter(articlesFilter)
  }, [articlesFilter])

  useEffect(() => {
    setFilter(SYSTEMS_COLUMN_ID, systemFilter)
  }, [systemFilter])

  // we don't show the order related info if the article is archived
  const hasOrderRelatedNote = state.globalFilter
    ? rows.map((row) => row.original).some((art) => art.state && art.state.orderRelated && !art.state.archived)
    : data.some((art) => art.state && art.state.orderRelated && !art.state.archived)

  const hasMakeToOrderNote = headerGroups?.some(
    (group) =>
      group.headers?.some((header) => header.id === attributeIDs.MAKE_TO_ORDER) &&
      !(articles.articlesListSize === 1 && articles.articlesList[0].state.archived)
  )
  const showOrderRelatedInfo = hasOrderRelatedNote || hasMakeToOrderNote

  // Define when to show the sticky header based on scroll position
  const startArticleTableEntry = useIntersectionObserver(startArticleTableRef, { rootMargin: "-48px" })
  const articleTableContainerEntry = useIntersectionObserver(tableContainerRef, { rootMargin: "-50px" })
  const isFixedHeaderVisible = !startArticleTableEntry?.isIntersecting && articleTableContainerEntry?.isIntersecting

  const rowsLength = rows.length
  /* Number of rows that add special styling to the table only in Firefox,
  to prevent the overflow bug in the three dot menu (we chose 5 to have some 
  room for de-DE that has more menu options) */
  const NUMBER_OF_ROWS = 5
  // Get number of rows that don't need the special styling for Firefox
  const notAffectedRows = rowsLength - NUMBER_OF_ROWS
  const isAffectedRow =
    activeArticleTableRow > -1 && (rowsLength < NUMBER_OF_ROWS || activeArticleTableRow >= notAffectedRows)

  /* Reset the scroll position of the sticky header and table container while we are in Firefox.
  We run this funtion when the user opens a three dot menu in the article table */
  const resetScroll = (rowIndex) => {
    if (window.navigator.userAgent.match(/firefox|fxios/i)) {
      if (rowIndex >= notAffectedRows) {
        tableContainerRef.current.scrollLeft = -1
        tableHeaderRef.current.scrollLeft = -1
        if (isScrollableLeft) {
          // Reset the shadow effect
          setScrollableLeft(false)
          setScrollableRight(true)
        }
      }
    }
  }

  return (
    <>
      {/* Dummy div for the beginning of the article table to attach the observer to it */}
      <div ref={startArticleTableRef}></div>
      {rowsLength ? (
        <>
          <ArticleTable
            {...getTableProps()}
            width={viewportWidth}
            className={`
              ${isScrollableRight ? "table--scrollable-right" : ""}
              ${isScrollableLeft ? "table--scrollable-left" : ""}
            `}
          >
            <TableBody
              className={`
                ${isAffectedRow ? "table--add-overflow-clip" : ""}
                ${(isScrollableRight || isScrollableLeft) && isAffectedRow ? "table--extra-padding-bot" : ""}
              `}
              // Prop used for styling
              extraPaddingVal={tableContainerRef.current?.offsetHeight - tableContainerRef.current?.scrollHeight}
              {...getTableBodyProps()}
              onScroll={handleTableScroll}
              ref={tableContainerRef}
            >
              {/* One sticky and one non-sticky header. Both are needed to be rendered on the same page
              so when the user changes the width of the vieport both headers are resized accordingly */}
              <Header
                headerGroups={headerGroups}
                width={viewportWidth}
                isFixed={true}
                isVisible={isFixedHeaderVisible}
                ref={tableHeaderRef}
                onScroll={handleTableHeaderScroll}
              />
              <Header headerGroups={headerGroups} width={viewportWidth} isFixed={false} isVisible={true} />

              <AnimatePresence>
                {rows.map((row, index) => {
                  prepareRow(row)
                  return (
                    <Row
                      key={row.original.articleCode}
                      row={row}
                      viewportWidth={viewportWidth}
                      contentWidth={contentWidth}
                      availableZipOptionsFormData={row.original.availableZipOptionsFormData}
                      isHighlightedFromSearch={highlightedArticles?.includes(row.original.articleCode)}
                      resetScroll={resetScroll}
                      rowNumberVal={index}
                    />
                  )
                })}
              </AnimatePresence>
            </TableBody>
          </ArticleTable>
          {showOrderRelatedInfo && (
            <>
              <Spacer axis="vertical" size={24} />
              <OrderRelatedInfo hasOrderRelatedNote={hasOrderRelatedNote} hasMakeToOrderNote={hasMakeToOrderNote} />
            </>
          )}
        </>
      ) : (
        <Feedback type="warning">{t("pdp-article-filter-no-results")}</Feedback>
      )}
    </>
  )
}

Table.propTypes = {
  articles: PropTypes.object,
  viewportEl: PropTypes.any,
  widthOffsets: PropTypes.number,
  highlightedArticles: PropTypes.array,
}

export default Table
